import { trigger, transition, style, animate } from '@angular/animations';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  Input,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  NgbCarousel,
  NgbCarouselConfig,
  NgbSlideEvent,
  NgbModule,
  NgbCarouselModule,
} from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import {
  CmsContentService,
  ChannelsType,
} from '@digital-platform/rs-cms/services';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';

@Component({
  selector: 'digital-platform-alert-banner',
  templateUrl: './alert-banner.component.html',
  styleUrls: ['./alert-banner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, NgbModule, NgbCarouselModule, IconComponent],
  animations: [
    trigger('accordionOpenClose', [
      transition(':enter', [
        style({ opacity: 0, height: 0 }),
        animate(
          '0.25s ease-out',
          style({ marginBottom: '-10px', opacity: 1, height: '*' })
        ),
      ]),
      transition(':leave', [
        animate('0.20s', style({ opacity: 0, height: 0 })),
      ]),
    ]),
  ],
})
export class AlertBannerComponent implements OnInit, OnDestroy {
  // Used for testing
  @Input() public limitSingleMessage = false;
  @Input() public useDevData = false;

  public config = inject(NgbCarouselConfig);

  private cmsContentService = inject(CmsContentService);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    this.config.showNavigationArrows = false;
    this.config.showNavigationIndicators = false;
  }

  channels: ChannelsType[] = [];

  paused = false;

  currentSlide = 0;
  showBanner = true;
  showMobileAlerts = false;
  isBrowser = isPlatformBrowser(this.platformId);

  private destroyed$ = new Subject<void>();
  @ViewChild('carousel', { static: false }) carousel!: NgbCarousel;

  // Used for testing
  sampleMessage = [
    {
      body: 'Our thoughts are with those impacted by inclement weather. If you are in an area that has been affected, please visit our \u003Ca href=\u0022/service-alerts\u0022 target=\u0022_blank\u0022\u003EAlerts\u003C/a\u003E page for the latest updates.',
      enabled: 'true',
      pattern: '^\\/(\\b(?!service-alerts$).*\\b)?$',
      zipCodes: '',
      session: '',
      severity: 'alert',
    },
    {
      body: 'COVID-19 Alert: The recent surge in residential waste creates the need for potential changes to our services in some communities. Before you submit a new request, please \u003Ca href=\u0022/coronavirus\u0022\u003Eclick here\u003C/a\u003E to see what services are impacted.\u0026nbsp;',
      enabled: 'true',
      pattern: '^\\/account\\/services\\/missed-pickup$',
      zipCodes: '',
      session: '',
      severity: 'info',
    },
  ];

  ngOnInit() {
    this.getAlerts();
  }

  getAlerts(): void {
    if (!this.useDevData) {
      this.cmsContentService
        .getBannerAlerts()
        .pipe(take(1))
        .subscribe((data) => {
          this.channels = data;
        });
    } else {
      if (this.limitSingleMessage) {
        this.channels = [this.sampleMessage[0]];
      } else {
        this.channels = this.sampleMessage;
      }
    }
  }

  onSlide(slideEvent: NgbSlideEvent): void {
    this.currentSlide = this.getCurrentSlideNumber(slideEvent);
  }

  // Bootstrap carousel does not have an event that passes the current slide number, so we have to extrapolate it
  getCurrentSlideNumber(slideEvent: NgbSlideEvent): number {
    const arraySplitByHyphen = slideEvent.current.split('-');
    const numberPosition = arraySplitByHyphen.length - 1;
    const currentSlideNumber = parseInt(arraySplitByHyphen[numberPosition], 10);

    // Sometimes we'll get stuff like "8 of 2" in the slide counter and I don't know why, so I did this
    if (currentSlideNumber > this.channels.length) {
      return 0;
    }

    return currentSlideNumber;
  }

  pauseCarousel(shouldPause: boolean): void {
    if (shouldPause) {
      this.carousel.pause();
    } else {
      this.carousel.cycle();
    }
  }

  nextMessage(event: Event): void {
    event.preventDefault();

    this.carousel.next();
  }

  previousMessage(event: Event): void {
    event.preventDefault();

    this.carousel.prev();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
